import React, { useEffect } from 'react';
import { graphql } from 'gatsby';
import { KiteIcon } from '@kite/react-kite';
import Layout from '../../layout/Layout';
import { getParagraph } from '../../components/componentLookup';
import { SEO } from '../../layout/SEO';
import { SmartLink } from '../../components/SmartLink/SmartLink';
import { Hero } from '../../components/Hero/Hero';
import { getImage } from 'gatsby-plugin-image';
import { getVideoId } from '../../shared/getVideoId';
import { PromoBarInputs } from '../../layout/Header/PromoBar/PromoBar';
import { processMetatags } from '../../shared/metaTags';
import { makeAnalyticsCall } from '../../shared/analytics/callAnalytics';
import { DriftChatButton } from '../../components/DriftChatButton/DriftChatButton';
import { EventName } from '../../models/GTMEventTypes';
import { processSchema } from '../../shared/Schema/Schema';

export default function LandingPage({ data }) {
    const page =
        data.nodeLandingPage ??
        data.nodeCategoryPage ??
        data.nodeProductPage ??
        data.nodeIndustryPage ??
        data.nodeCityPage;
    let { field_hero: heroSettings } = page?.relationships ?? {};
    const paragraphs = page.relationships?.field_components?.map((paragraph) =>
        getParagraph(paragraph)
    );
    const customSchemas = page.relationships?.field_schema;
    let promoBar: PromoBarInputs = null;
    if (page.relationships?.field_promo_bar) {
        promoBar = {
            text: page.relationships.field_promo_bar.field_content_text?.value,
            header: page.relationships.field_promo_bar.field_heading,
        };
    }

    let pageType = `Basic Page`;

    if (page.relationships?.field_page_type?.name) {
        pageType = page.relationships?.field_page_type?.name;
    } else {
        if (data.nodeCategoryPage) {
            pageType = `Category`;
        } else if (data.nodeProductPage) {
            pageType = `Product`;
        } else if (data.nodeIndustryPage) {
            pageType = `Industry`;
        } else if (data.nodeCityPage) {
            pageType = `City`;
            heroSettings = {
                field_column_type: `two`,
                field_heading: page?.field_heading,
                field_subtitle: page?.field_subheading,
                field_lr_align: `left`,
                field_theme: `white`,
                relationships: {
                    field_media: {
                        relationships: {
                            field_media_image: {
                                localFile:
                                    page?.relationships?.field_hero_image
                                        ?.relationships?.field_media_image
                                        ?.localFile,
                            },
                        },
                    },
                },
            };
        }
    }

    useEffect(() => {
        makeAnalyticsCall(EventName.PAGE_VIEW, {
            pageType,
        });
        processSchema(customSchemas);
    }, []);

    return (
        <Layout
            promoBar={promoBar}
            isHomepage={page.relationships.field_hero?.field_homepage}
        >
            {heroSettings && (
                <Hero
                    cols={heroSettings.field_column_type === `two` ? 2 : 1}
                    heading={heroSettings.field_heading}
                    subheading={heroSettings.field_subtitle}
                    eyebrow={heroSettings.field_eyebrow_text}
                    callsToAction={heroSettings.field_link}
                    homepage={heroSettings.field_homepage}
                    align={heroSettings.field_lr_align?.toLowerCase()}
                    theme={heroSettings.field_theme?.toLowerCase()}
                    heroImage={getImage(
                        heroSettings.relationships?.field_media?.relationships
                            ?.field_media_image?.localFile
                    )}
                    heroImageAlt={
                        heroSettings.relationships?.field_media
                            ?.field_media_image?.alt
                    }
                    heroVideo={
                        heroSettings.relationships?.field_media
                            ?.field_media_oembed_video
                            ? getVideoId(
                                  heroSettings.relationships?.field_media
                                      ?.field_media_oembed_video
                              )
                            : undefined
                    }
                    videoName={
                        heroSettings.relationships?.field_media?.name
                            ? heroSettings.relationships?.field_media?.name
                            : undefined
                    }
                >
                    {pageType === `City` && (
                        <>
                            <ul>
                                <li className="list-address">
                                    <span className="map-icon-container">
                                        <KiteIcon
                                            size="1.3rem"
                                            icon="ki-map-point-f"
                                        />
                                    </span>
                                    <span>{page.title}</span>
                                </li>
                                <li>
                                    <KiteIcon
                                        size="1.3rem"
                                        icon="ki-compose-f"
                                    />
                                    <SmartLink href="/contact">
                                        Get in touch
                                    </SmartLink>
                                </li>
                                <li>
                                    <KiteIcon size="1.3rem" icon="ki-chat-f" />
                                    <DriftChatButton />
                                </li>
                                <li>
                                    <KiteIcon size="1.3rem" icon="ki-phone-f" />
                                    <SmartLink href="tel:+1-844-To-Reach">
                                        1-844-To-Reach
                                    </SmartLink>
                                </li>
                            </ul>
                            <span>
                                Looking for details on another area?{` `}
                                <SmartLink href="/markets-map">
                                    View the Markets Map
                                </SmartLink>
                            </span>
                        </>
                    )}
                </Hero>
            )}
            {paragraphs}
        </Layout>
    );
}

export const Head = ({ data }) => {
    const page =
        data.nodeLandingPage ??
        data.nodeCategoryPage ??
        data.nodeProductPage ??
        data.nodeIndustryPage ??
        data.nodeCityPage;
    return (
        <>
            <SEO location={page.fields.url}>
                {processMetatags(page.metatag)}
            </SEO>
        </>
    );
};

export const query = graphql`
    query ($id: String!) {
        nodeLandingPage(id: { eq: $id }) {
            fields {
                url
            }
            title
            drupal_internal__nid
            drupal_internal__vid
            moderation_state
            metatag {
                tag
                attributes {
                    content
                    href
                    name
                    rel
                    property
                }
            }
            relationships {
                field_schema {
                    type: __typename
                    ...paragraph__custom_schema
                }
                field_promo_bar {
                    ...paragraph__promo_bar
                }
                field_hero {
                    ...paragraph__hero
                }
                field_components {
                    type: __typename
                    ...paragraph__article_inline_callout
                    ...paragraph__article_sources_list
                    ...paragraph__award_primary_link
                    ...paragraph__award_secondary_list
                    ...paragraph__award_tertiary_link
                    ...paragraph__dynamic_case_study_preview
                    ...paragraph__case_study_metrics
                    ...paragraph__city_block
                    ...paragraph__components_anchor
                    ...paragraph__contact_us_block
                    ...paragraph__embedded_image
                    ...paragraph__embedded_video
                    ...paragraph__external_form
                    ...paragraph__four_column
                    ...paragraph__header_body_collection
                    ...paragraph__infographic
                    ...paragraph__newsletter_signup
                    ...paragraph__logo
                    ...paragraph__quiz
                    ...paragraph__quote
                    ...paragraph__recommended_articles
                    ...paragraph__single_col_custom_btns
                    ...paragraph__single_column_dynamic_form
                    ...paragraph__single_column_text
                    ...paragraph__three_column
                    ...paragraph__three_column_narrow
                    ...paragraph__two_column
                    ...paragraph__two_column_dynamic_form
                    ...paragraph__two_column_form_text
                    ...paragraph__two_column_text_image
                    ...paragraph__two_column_text_media
                }
                field_page_type {
                    name
                }
            }
        }
        nodeProductPage(id: { eq: $id }) {
            fields {
                url
            }
            title
            drupal_internal__nid
            drupal_internal__vid
            moderation_state
            metatag {
                tag
                attributes {
                    content
                    href
                    name
                    rel
                    property
                }
            }
            relationships {
                field_schema {
                    type: __typename
                    ...paragraph__custom_schema
                }
                field_hero {
                    ...paragraph__hero
                }
                field_promo_bar {
                    ...paragraph__promo_bar
                }
                field_components {
                    type: __typename
                    ...paragraph__article_inline_callout
                    ...paragraph__article_sources_list
                    ...paragraph__award_primary_link
                    ...paragraph__award_secondary_list
                    ...paragraph__award_tertiary_link
                    ...paragraph__dynamic_case_study_preview
                    ...paragraph__case_study_metrics
                    ...paragraph__city_block
                    ...paragraph__components_anchor
                    ...paragraph__contact_us_block
                    ...paragraph__embedded_image
                    ...paragraph__embedded_video
                    ...paragraph__external_form
                    ...paragraph__four_column
                    ...paragraph__header_body_collection
                    ...paragraph__infographic
                    ...paragraph__logo
                    ...paragraph__quiz
                    ...paragraph__newsletter_signup
                    ...paragraph__quote
                    ...paragraph__recommended_articles
                    ...paragraph__single_col_custom_btns
                    ...paragraph__single_column_dynamic_form
                    ...paragraph__single_column_text
                    ...paragraph__three_column
                    ...paragraph__three_column_narrow
                    ...paragraph__two_column
                    ...paragraph__two_column_dynamic_form
                    ...paragraph__two_column_form_text
                    ...paragraph__two_column_text_image
                    ...paragraph__two_column_text_media
                }
            }
        }

        nodeIndustryPage(id: { eq: $id }) {
            fields {
                url
            }
            title
            drupal_internal__nid
            drupal_internal__vid
            moderation_state
            metatag {
                tag
                attributes {
                    content
                    href
                    name
                    rel
                    property
                }
            }
            relationships {
                field_schema {
                    type: __typename
                    ...paragraph__custom_schema
                }
                field_hero {
                    ...paragraph__hero
                }
                field_promo_bar {
                    ...paragraph__promo_bar
                }
                field_components {
                    type: __typename
                    ...paragraph__article_inline_callout
                    ...paragraph__article_sources_list
                    ...paragraph__award_primary_link
                    ...paragraph__award_secondary_list
                    ...paragraph__award_tertiary_link
                    ...paragraph__dynamic_case_study_preview
                    ...paragraph__case_study_metrics
                    ...paragraph__city_block
                    ...paragraph__components_anchor
                    ...paragraph__contact_us_block
                    ...paragraph__embedded_image
                    ...paragraph__embedded_video
                    ...paragraph__external_form
                    ...paragraph__four_column
                    ...paragraph__header_body_collection
                    ...paragraph__infographic
                    ...paragraph__logo
                    ...paragraph__quiz
                    ...paragraph__newsletter_signup
                    ...paragraph__quote
                    ...paragraph__recommended_articles
                    ...paragraph__single_col_custom_btns
                    ...paragraph__single_column_dynamic_form
                    ...paragraph__single_column_text
                    ...paragraph__three_column
                    ...paragraph__three_column_narrow
                    ...paragraph__two_column
                    ...paragraph__two_column_dynamic_form
                    ...paragraph__two_column_form_text
                    ...paragraph__two_column_text_image
                    ...paragraph__two_column_text_media
                }
            }
        }

        nodeCategoryPage(id: { eq: $id }) {
            fields {
                url
            }
            title
            drupal_internal__nid
            drupal_internal__vid
            moderation_state
            metatag {
                tag
                attributes {
                    content
                    href
                    name
                    rel
                    property
                }
            }
            relationships {
                field_schema {
                    type: __typename
                    ...paragraph__custom_schema
                }
                field_hero {
                    ...paragraph__hero
                }
                field_promo_bar {
                    ...paragraph__promo_bar
                }
                field_components {
                    type: __typename
                    ...paragraph__article_sources_list
                    ...paragraph__article_inline_callout
                    ...paragraph__award_primary_link
                    ...paragraph__award_secondary_list
                    ...paragraph__award_tertiary_link
                    ...paragraph__dynamic_case_study_preview
                    ...paragraph__case_study_metrics
                    ...paragraph__city_block
                    ...paragraph__components_anchor
                    ...paragraph__contact_us_block
                    ...paragraph__embedded_image
                    ...paragraph__embedded_video
                    ...paragraph__external_form
                    ...paragraph__four_column
                    ...paragraph__header_body_collection
                    ...paragraph__infographic
                    ...paragraph__logo
                    ...paragraph__quiz
                    ...paragraph__newsletter_signup
                    ...paragraph__quote
                    ...paragraph__recommended_articles
                    ...paragraph__single_col_custom_btns
                    ...paragraph__single_column_dynamic_form
                    ...paragraph__single_column_text
                    ...paragraph__three_column
                    ...paragraph__three_column_narrow
                    ...paragraph__two_column
                    ...paragraph__two_column_dynamic_form
                    ...paragraph__two_column_form_text
                    ...paragraph__two_column_text_image
                    ...paragraph__two_column_text_media
                }
            }
        }
        nodeCityPage(id: { eq: $id }) {
            field_heading
            field_subheading
            title
            drupal_internal__nid
            drupal_internal__vid
            moderation_state
            field_zipcodes
            metatag {
                tag
                attributes {
                    content
                    href
                    name
                    rel
                    property
                }
            }
            fields {
                url
            }
            relationships {
                field_schema {
                    type: __typename
                    ...paragraph__custom_schema
                }
                field_hero_image {
                    field_media_image {
                        alt
                    }
                    relationships {
                        field_media_image {
                            localFile {
                                childImageSharp {
                                    gatsbyImageData
                                }
                            }
                        }
                    }
                }
                field_promo_bar {
                    ...paragraph__promo_bar
                }
                field_components {
                    type: __typename
                    ...paragraph__article_inline_callout
                    ...paragraph__article_sources_list
                    ...paragraph__award_primary_link
                    ...paragraph__award_secondary_list
                    ...paragraph__award_tertiary_link
                    ...paragraph__dynamic_case_study_preview
                    ...paragraph__case_study_metrics
                    ...paragraph__city_block
                    ...paragraph__components_anchor
                    ...paragraph__contact_us_block
                    ...paragraph__embedded_image
                    ...paragraph__embedded_video
                    ...paragraph__external_form
                    ...paragraph__four_column
                    ...paragraph__header_body_collection
                    ...paragraph__infographic
                    ...paragraph__logo
                    ...paragraph__quiz
                    ...paragraph__newsletter_signup
                    ...paragraph__quote
                    ...paragraph__recommended_articles
                    ...paragraph__single_col_custom_btns
                    ...paragraph__single_column_dynamic_form
                    ...paragraph__single_column_text
                    ...paragraph__three_column
                    ...paragraph__three_column_narrow
                    ...paragraph__two_column
                    ...paragraph__two_column_dynamic_form
                    ...paragraph__two_column_form_text
                    ...paragraph__two_column_text_image
                    ...paragraph__two_column_text_media
                }
            }
        }
    }
`;
